import { useState, useEffect } from 'react'
import { AlertTriangle, Eye, FileText, CheckCircle, Save, ImageIcon, X, Loader2, Sparkles } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useApi } from '@/hooks/use-api'
import type { Post } from './types'
import { useUpdatePost, useApprovePost } from './use-content'

const MAX_CHARS = 3000

// Parse hashtags from content string
function parseHashtags(content: string): readonly string[] {
  const matches = content.match(/#[\w]+/g) ?? []
  return [...new Set(matches.map((h) => h.slice(1)))]
}

// Detect simple guardrail violations inline
function detectViolations(content: string): readonly string[] {
  const violations: string[] = []
  const lower = content.toLowerCase()

  const prohibitedPhrases = [
    'guaranteed results',
    'get rich quick',
    'make money fast',
    'mlm',
    'pyramid scheme',
    '100% free',
    'act now',
    'limited time offer',
  ]
  for (const phrase of prohibitedPhrases) {
    if (lower.includes(phrase)) {
      violations.push(`Prohibited phrase detected: "${phrase}"`)
    }
  }

  // Placeholder detection
  if (/\[(?:FIRST_NAME|LAST_NAME|COMPANY|INSERT HERE|YOUR_NAME)\]/i.test(content)) {
    violations.push('Unresolved placeholder detected — fill in before publishing')
  }

  // ALL CAPS check (>40% uppercase non-space chars)
  const letters = content.replace(/\s/g, '')
  if (letters.length > 0) {
    const upperCount = letters.split('').filter((c) => c >= 'A' && c <= 'Z').length
    if (upperCount / letters.length > 0.4) {
      violations.push('Content appears to be mostly uppercase — LinkedIn may flag this as spam')
    }
  }

  return violations
}

// LinkedIn post preview styling
function LinkedInPreview({ content, mediaUrl }: { readonly content: string; readonly mediaUrl?: string | null }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm ">
      <div className="mb-3 flex items-center gap-2.5">
        <div className="h-10 w-10 rounded-full bg-blue-600" />
        <div>
          <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Your Name</p>
          <p className="text-xs text-zinc-500">Your Headline • Now</p>
        </div>
      </div>
      <p className="whitespace-pre-line text-sm leading-relaxed text-zinc-800 dark:text-zinc-200">
        {content || <span className="text-zinc-400 italic">Start typing to see preview...</span>}
      </p>
      {mediaUrl && (
        <img
          src={mediaUrl}
          alt="Post attachment"
          className="mt-3 max-h-80 w-full rounded-lg border object-cover"
        />
      )}
      {content && (
        <div className="mt-3 flex items-center gap-4 border-t pt-3 text-xs text-zinc-500">
          <span>👍 Like</span>
          <span>💬 Comment</span>
          <span>↗️ Share</span>
        </div>
      )}
    </div>
  )
}

type PostEditorProps = {
  readonly post: Post | null
  readonly open: boolean
  readonly onClose: () => void
}

export function PostEditor({ post, open, onClose }: PostEditorProps) {
  const [content, setContent] = useState('')
  const [activePanel, setActivePanel] = useState<'edit' | 'preview'>('edit')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [mediaUrl, setMediaUrl] = useState<string | null>(null)
  const [showImageTools, setShowImageTools] = useState(false)
  const [imagePrompt, setImagePrompt] = useState('')
  const [isGeneratingImage, setIsGeneratingImage] = useState(false)
  const [imageError, setImageError] = useState<string | null>(null)

  const apiFetch = useApi()
  const { mutateAsync: updatePost, isPending: isUpdating } = useUpdatePost()
  const { mutateAsync: approvePost, isPending: isApproving } = useApprovePost()

  useEffect(() => {
    if (post && open) {
      setContent(post.content)
      setSaveError(null)
      setMediaUrl(post.media_url ?? null)
      setShowImageTools(false)
      setImagePrompt(post.topic ? `Editorial LinkedIn image about ${post.topic}, clean and modern` : '')
      setImageError(null)
    }
  }, [post, open])

  async function handleGenerateImage() {
    if (isGeneratingImage || imagePrompt.trim().length < 3) return
    setIsGeneratingImage(true)
    setImageError(null)
    try {
      const res = await apiFetch<{ image_url: string }>(
        '/api/settings/integrations/generate-image',
        { method: 'POST', body: { prompt: imagePrompt.trim() } },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Generation failed')
      setMediaUrl(res.data.image_url)
    } catch (err) {
      setImageError(err instanceof Error ? err.message : 'Generation failed')
    } finally {
      setIsGeneratingImage(false)
    }
  }

  if (!post) return null

  const charsRemaining = MAX_CHARS - content.length
  const hashtags = parseHashtags(content)
  const violations = detectViolations(content)
  const isOverLimit = content.length > MAX_CHARS

  function handleClose() {
    if (isUpdating || isApproving) return
    onClose()
  }

  async function handleSaveDraft() {
    if (!post) return
    setSaveError(null)
    try {
      // A scheduled post keeps its slot: saving edits used to flip it back to
      // draft, silently cancelling the publish.
      const keepStatus = post.status === 'scheduled'
      await updatePost({
        id: post.id,
        updates: keepStatus ? { content, media_url: mediaUrl } : { content, status: 'draft', media_url: mediaUrl },
      })
      onClose()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed')
    }
  }

  async function handleApprove() {
    if (!post) return
    setSaveError(null)
    try {
      // Save content first, then approve
      await updatePost({ id: post.id, updates: { content, media_url: mediaUrl } })
      await approvePost(post.id)
      onClose()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Approval failed')
    }
  }

  const canApprove = violations.length === 0 && !isOverLimit && content.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>Edit Post</DialogTitle>
          <DialogDescription>
            {post.topic && <span className="font-medium">{post.topic}</span>}
            {post.topic && post.content_type && ' · '}
            {post.content_type && <span className="capitalize">{post.content_type.replace(/_/g, ' ')}</span>}
          </DialogDescription>
        </DialogHeader>

        {/* Panel toggle */}
        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setActivePanel('edit')}
            className={cn(
              'flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors',
              activePanel === 'edit'
                ? 'border-b-2 border-cobalt-bright text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <FileText className="h-4 w-4" />
            Editor
          </button>
          <button
            type="button"
            onClick={() => setActivePanel('preview')}
            className={cn(
              'flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors',
              activePanel === 'preview'
                ? 'border-b-2 border-cobalt-bright text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Eye className="h-4 w-4" />
            LinkedIn Preview
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-6">
          {activePanel === 'edit' ? (
            <div className="space-y-4">
              {/* Textarea */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Content</label>
                  <span
                    className={cn(
                      'text-xs',
                      isOverLimit ? 'font-medium text-destructive' : 'text-muted-foreground',
                    )}
                  >
                    {charsRemaining < 0
                      ? `${Math.abs(charsRemaining)} over limit`
                      : `${charsRemaining} remaining`}
                  </span>
                </div>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className={cn(
                    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed placeholder:text-subtle-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none',
                    isOverLimit && 'border-destructive focus:ring-destructive',
                  )}
                  rows={14}
                  placeholder="Write your LinkedIn post here..."
                />
              </div>

              {/* Hashtag suggestions */}
              {hashtags.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">
                    Detected hashtags ({hashtags.length})
                    {hashtags.length > 5 && (
                      <span className="ml-1 text-amber-300 ">
                        — LinkedIn recommends max 5
                      </span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {hashtags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Post image (BYO image generation, Pro) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Image</label>
                  {!mediaUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1.5 text-xs"
                      onClick={() => setShowImageTools((v) => !v)}
                    >
                      <ImageIcon className="h-3.5 w-3.5" />
                      {showImageTools ? 'Hide image tools' : 'Add an image'}
                    </Button>
                  )}
                </div>
                {mediaUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={mediaUrl}
                      alt="Post attachment"
                      className="max-h-56 rounded-lg border object-contain"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute right-2 top-2 h-7 gap-1 px-2 text-xs"
                      onClick={() => setMediaUrl(null)}
                    >
                      <X className="h-3 w-3" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  showImageTools && (
                    <div className="space-y-2 rounded-md border border-dashed p-3">
                      <p className="text-xs text-muted-foreground">
                        Generate an image with your connected image API (Settings, Integrations).
                        It publishes to LinkedIn together with the post.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          value={imagePrompt}
                          onChange={(e) => setImagePrompt(e.target.value)}
                          placeholder="Describe the image you want"
                          disabled={isGeneratingImage}
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => void handleGenerateImage()}
                          disabled={isGeneratingImage || imagePrompt.trim().length < 3}
                          className="shrink-0 gap-1.5"
                        >
                          {isGeneratingImage ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                          )}
                          Generate
                        </Button>
                      </div>
                      {imageError && <p className="text-xs text-destructive">{imageError}</p>}
                    </div>
                  )
                )}
              </div>

              {/* Guardrail violations */}
              {violations.length > 0 && (
                <div className="rounded-md border border-red-500/25 bg-red-500/10 p-3 ">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-300 dark:text-red-400" />
                    <div>
                      <p className="text-sm font-medium text-red-800 ">
                        Guardrail issues — fix before approving
                      </p>
                      <ul className="mt-1 list-disc list-inside space-y-0.5">
                        {violations.map((v) => (
                          <li key={v} className="text-xs text-red-300 dark:text-red-400">
                            {v}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Save error */}
              {saveError && (
                <p className="text-sm text-destructive">{saveError}</p>
              )}
            </div>
          ) : (
            <LinkedInPreview content={content} mediaUrl={mediaUrl} />
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between border-t px-6 py-4">
          <Button variant="outline" onClick={handleClose} disabled={isUpdating || isApproving}>
            Cancel
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              // Was `onClick={void handleSaveDraft}`, which evaluates to
              // undefined: Save (and Approve below) did nothing, so edits
              // and attached images were never saved.
              onClick={() => void handleSaveDraft()}
              disabled={isUpdating || isApproving || isOverLimit}
              className="gap-1.5"
            >
              <Save className="h-4 w-4" />
              {isUpdating ? 'Saving...' : post.status === 'scheduled' ? 'Save changes' : 'Save Draft'}
            </Button>
            {post.status !== 'scheduled' && (
            <Button
              onClick={() => void handleApprove()}
              disabled={!canApprove || isUpdating || isApproving}
              className="gap-1.5"
            >
              <CheckCircle className="h-4 w-4" />
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
